import { FormControl, FormGroup } from '@angular/forms';
import { Namespace, PromptModule } from '@vendasta/protos/ai_assistants/v1alpha1';
import { FormArrayWithDirtyTracking } from './forms';
import { createTemplateValidator } from './forms.validators';

// Prompt Module
export type AiPromptModuleControls = {
  id: FormControl<string | null>;
  namespace: FormControl<Namespace | null>;
  instructions: FormControl<string | null>;
  managed: FormControl<boolean>;
};

export class AiPromptModuleForm extends FormGroup<AiPromptModuleControls> {
  private originalContents: string | undefined;

  constructor(opts: { promptModule?: Partial<PromptModule>; contents?: string }) {
    const { promptModule, contents } = opts;

    super({
      id: new FormControl({ value: promptModule?.id ?? null, disabled: true }),
      namespace: new FormControl({ value: promptModule?.namespace ?? null, disabled: true }),
      instructions: new FormControl(contents ?? null, {
        asyncValidators: [createTemplateValidator()],
      }),
      managed: new FormControl({ value: promptModule?.managed ?? false, disabled: true }, { nonNullable: true }),
    });
    this.originalContents = contents;
  }

  override get dirty() {
    const currentValue = this.controls.instructions.getRawValue();
    // Treat falsy values the same (yes we could do != instead, but this way it's harder for someone to come along and change it by accident)
    return (currentValue || null) !== (this.originalContents || null);
  }

  override markAsPristine(): void {
    super.markAsPristine();
    const value = this.controls.instructions.getRawValue();
    this.originalContents = value === null ? undefined : value;
  }

  toPromptModule(localNamespace: Namespace, name?: string | null): Partial<PromptModule> {
    return {
      id: this.controls.id.getRawValue() ?? undefined,
      name: name ?? undefined,
      namespace: this.controls.namespace.getRawValue() ?? localNamespace,
      managed: this.controls.managed.getRawValue() ?? false,
    };
  }

  getContent(): string | undefined {
    return this.controls.instructions.getRawValue() ?? undefined;
  }
}

export class AiPromptModuleFormArray extends FormArrayWithDirtyTracking<AiPromptModuleForm> {
  constructor(opts: { promptModules: Partial<PromptModule>[]; contents: Record<string, string | undefined> }) {
    const { promptModules, contents } = opts;

    const controls = promptModules.map((pm) => {
      const content = pm.id ? contents[pm.id] : undefined;
      return new AiPromptModuleForm({ promptModule: pm, contents: content });
    });

    if (controls.length === 0) {
      controls.push(new AiPromptModuleForm({}));
    }
    super(controls);
  }

  getPromptModuleKeys(fallbackNamespace: Namespace): Partial<PromptModule>[] {
    return this.controls
      .map((pm) => {
        return {
          id: pm.controls.id.getRawValue() ?? undefined,
          namespace: pm.controls.namespace.getRawValue() ?? fallbackNamespace,
        };
      })
      .filter((pm) => !!pm.id);
  }
}
